import React from "react";
import { createTheme } from "@mui/material/styles";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

// const raleway = {
//   fontFamily: 'Poppins',
//   fontStyle: 'normal',
//   fontDisplay: 'swap',
//   fontWeight: 400,
//   src: `
//     local('Poppins'),
//     local('Poppins-Regular'),
//     url(${RalewayWoff2}) format('woff2')
//   `,
//   unicodeRange:
//     'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF',
// };

const theme = createTheme({
  palette: {
    primary: {
      main: "rgb(50, 102, 139)",
      contrastText: "rgb(242, 247, 252)",
    },
    secondary: {
      main: "rgb(255, 255, 252)",
      contrastText: "rgb(50, 102, 139)",
    },
    error: {
      main: "rgb(250, 79, 1)",
      contrastText: "rgb(255, 228, 215)",
    },
    action: {
      hover: "rgba(222, 237, 248, 0.6)",
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
    background: {
      default: "rgb(242, 247, 252)",
    },
  },
  typography: {
    // fontSize: 13,
    fontFamily: 'Poppins, Arial, sans-serif',

    fontWeightBold: 900,
    h6: {
      fontWeight: 700,
    },
    subtitle1: {
      fontWeight: 600,
    },
  },
  components: {
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: "0.5rem 1rem",
          color: "rgb(242, 247, 252)",
          backgroundColor: "rgb(50, 102, 139)",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "0.5rem 1rem",
          backgroundColor: "rgb(242, 247, 252)",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "1rem",
          backgroundColor: "rgb(242, 247, 252)",
        },
        dividers: {
          padding: "1rem",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          padding: "0.5rem 1rem",
        },
        icon: {
          padding: "0.5rem 0",
        },
        message: {
          padding: "0.5rem 0",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          padding: "0.35rem 1.5rem",
          // minWidth: 100,
        },
        outlined: {
          padding: "0.25rem 1.5rem",
        },
        contained: {
          padding: "0.35rem 1.5rem",
        },
        containedSecondary: {
          "&:hover": {
            backgroundColor: "rgb(255, 255, 255, 0.8)",
          },
        },
        containedSizeSmall: {
          padding: "0.25rem 1rem",
        },
        outlinedSizeSmall: {
          padding: "0.2rem 1rem",
        },
        outlinedSizeLarge: {
          padding: "0.5rem 1rem",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: "0.5rem",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "0.125rem 0.5rem",
          borderBottom: "1px solid #0000001F",
        },
        head: {
          backgroundColor: "rgb(255, 255, 252)",
          color: "rgb(50, 102, 139)",
          fontWeight: 700,
          padding: "0.5rem",
          textTransform: "uppercase",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:nth-of-type(even)": {
            backgroundColor: "rgb(222 237 248)",
          },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          "&:hover": {
            color: "rgb(50, 102, 139, 0.65)",
          },
          "&:focus": {
            color: "rgb(50, 102, 139, 0.7)",
          },
          "&.Mui-active": {
            color: "rgb(23, 59, 85)",
          }
        }
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          padding: "0.25rem 0.5rem",
          height: "2rem",
          gap: "0.25rem",
        },
        sizeSmall: {
          padding: "0.125rem 0.25rem",
          height: "1.2rem",
        },
        sizeMedium: {
          padding: "0.25rem 0.5rem",
          height: "2rem",
        },
        label: {
          paddingLeft: "0.5rem",
          paddingRight: "0.5rem",
          fontSize: "0.875rem",
        },
        labelSmall: {
          paddingLeft: "0.25rem",
          paddingRight: "0.25rem",
          fontSize: "0.75rem",
        },
        icon: {
          marginLeft: 0
        },
        deleteIcon: {
          marginRight: 0
        }
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: "3rem",
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        img: {
          background:
            "linear-gradient(0deg, rgba(195,189,189,1) 0%, rgba(217,217,217,1) 46%)",
          objectPosition: "top center",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "0.875rem",
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--background)",
          padding: 0,
          gap: "1rem",
        },
      },
    },
    MuiStep: {
      styleOverrides: {
        horizontal: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },
  },
});

const ThemeProviderCustom = ({ children }) => {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          {children}
        </LocalizationProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default ThemeProviderCustom;
